import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {ServiceBase} from '../base/service.base';
import {UserService} from './user.service';

/**
 * Fatia 5C — REST da Creche (importação carga inicial / manutenção + exportação).
 * Tudo aqui é REST (não GraphQL). A `BASE_URL` do environment JÁ inclui o `/v1`,
 * então as URLs usam `${BASE_URL}/daycare/...` (NUNCA `/v1/daycare/...`).
 */
@Injectable({
    providedIn: 'root'
})
export class DaycareService extends ServiceBase {

    constructor(http: HttpClient, public userService: UserService) {
        super(http);
    }

    /**
     * Exporta o Excel canônico da unidade. A resposta é uma string base64
     * (data-URI) COM aspas literais grudadas — deve ser entregue direto ao
     * `BaseComponent.downloadHandlerFile(data)` (exige a âncora `#btnDownload`).
     * admin: `unityId` obrigatório; local-manager: o back força a unidade dele.
     */
    exportExcel(unityId?: string) {
        const url = `${this.BASE_URL}/daycare/export/excel`;
        return this.http.post(url, {unityId});
    }

    /**
     * Fatia 7 — export Excel do histórico de retiradas (mesmos filtros da query,
     * sem paginação). Resposta = string base64 data-URI com aspas literais →
     * `downloadHandlerFile`. Escopo por unidade imposto no back.
     */
    exportPickupExcel(filter: { unityId?: string, keyword?: string, startDate?: string, endDate?: string, status?: string }) {
        const url = `${this.BASE_URL}/daycare/export/pickup-excel`;
        return this.http.post(url, filter || {});
    }

    // ----- Carga inicial (formato legado) -----
    importLegacyPreview(file, unityId: string): Observable<any> {
        return this.uploadDaycare(file, '/daycare/import/legacy/preview', {unityId});
    }

    importLegacyApply(file, unityId: string): Observable<any> {
        // multipart → tudo vira string; o back aceita 'true'/'TRUE'/'1'.
        return this.uploadDaycare(file, '/daycare/import/legacy/apply', {unityId, acceptWarnings: 'true'});
    }

    // ----- Manutenção (formato canônico com IDs) -----
    importMaintenancePreview(file, unityId: string): Observable<any> {
        return this.uploadDaycare(file, '/daycare/import/maintenance/preview', {unityId});
    }

    importMaintenanceApply(file, unityId: string): Observable<any> {
        return this.uploadDaycare(file, '/daycare/import/maintenance/apply', {unityId, confirm: 'true'});
    }

    /**
     * Upload multipart genérico da Creche. Espelha o `ExcelService.upload` (XHR
     * cru, Bearer setado à mão — o interceptor NÃO cobre XHR cru), mas anexa
     * TODOS os campos de `fields` além do `file` (o `upload()` só manda o file).
     *
     * ⚠️ Erro (status != 200): o corpo vem em STRING; tentamos fazer JSON.parse
     * para expor a lista de erros do back (ex.: 400 = `{message}`, 409 do
     * guarda-corpo da carga inicial). Se não for JSON, devolve a string crua.
     */
    uploadDaycare(file, urlParameter: string, fields: { [key: string]: string } = {}): Observable<any> {
        const url = `${this.BASE_URL}${urlParameter}`;
        return new Observable((observer) => {
            const formData: FormData = new FormData();
            const xhr: XMLHttpRequest = new XMLHttpRequest();
            if (file != null) {
                formData.append('file', file, file.name);
            }
            Object.keys(fields).forEach((k) => {
                if (fields[k] != null) {
                    formData.append(k, fields[k]);
                }
            });
            xhr.onreadystatechange = () => {
                if (xhr.readyState === 4) {
                    if (xhr.status === 200) {
                        observer.next(JSON.parse(xhr.response));
                        observer.complete();
                    } else {
                        observer.error(this.parseError(xhr.response, xhr.status));
                    }
                }
            };
            xhr.open('POST', url, true);
            xhr.setRequestHeader('Authorization', 'Bearer ' + this.userService.getToken());
            xhr.send(formData);
        });
    }

    // Normaliza o corpo de erro do XHR cru (string) num objeto {status, body}.
    private parseError(raw: any, status: number): { status: number, body: any } {
        let body: any = raw;
        try {
            body = JSON.parse(raw);
        } catch (e) {
            body = {message: typeof raw === 'string' ? raw : 'Erro inesperado.'};
        }
        return {status, body};
    }
}
